import { AnimationEvent, animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ZippyDirective } from '@df/legacy/ui/zippy/zippy.directive';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollToPlugin);
}

export interface ZippyScrollIntoViewOptions {
    duration?: number;
    delay?: number;
    offsetY?: number;
}

/**
 * Zippy Hide UP animation
 *
 * Can be used for accordions.
 *
 * Notice: will automatically add `ng-hide-animate`
 *
 * @param {string} zippyHideUp - Zippy ID (name).
 * @param {number=} [zippyAnimationDuration=200] - Animation duration (up and down).
 * @param {boolean} zippyAnimationOff - Turns off the animation
 *
 * @example
 * ```html
 * <div zippyHideUp="zippyID">
 * ...
 * </div>
 * ```
 */
@Component({
    selector: '[zippyHideUp]',
    standalone: false,
    template: `
        <div
            [@zippyCollapse]="{
                value: playerState,
                params: {
                    animationDuration: zippyAnimationDuration
                }
            }"
            (@zippyCollapse.done)="zippyAnimationDone.next($event)"
        >
            <ng-content></ng-content>
        </div>
    `,
    styles: [
        `
            :host.zippy-hide-up {
                overflow: hidden;
                > div {
                    visibility: hidden;
                }
                &:not(.ng-hide-animate) > div:not(.ng-trigger-zippyCollapse) {
                    height: 0;
                }
            }
        `
    ],
    animations: [
        trigger('zippyCollapse', [
            state('collapsed, void', style({ height: '0px', visibility: 'hidden' })),
            state('expanded', style({ height: '*', visibility: 'visible' })),
            transition('expanded <=> collapsed, void => collapsed', animate('{{ animationDuration }}ms cubic-bezier(0.4,0.0,0.2,1)'))
        ])
    ]
})
export class ZippyHideUpComponent extends ZippyDirective implements OnInit, OnDestroy {
    playerState: 'expanded' | 'collapsed' = 'collapsed';
    /**
     * Respect zippy name attribute for name parsing.
     */
    @Input()
    set zippyHideUp(name: string | (string | number)[]) {
        this.parseName(name);
    }

    // Animation options
    // ---
    @Input()
    zippyAnimationDuration = 200;

    @Input()
    zippyAnimationOff?: boolean;

    @Input()
    zippyScrollIntoView?: ZippyScrollIntoViewOptions;

    /** An event emitted after the content expansion animation happens. */
    @Output()
    afterExpand = new EventEmitter<void>();

    /** An event emitted after the content collapse animation happens. */
    @Output()
    afterCollapse = new EventEmitter<void>();

    @HostBinding('class.zippy-hide-up')
    get isActiveHideUp() {
        return !this._zippyOff;
    }
    @HostBinding('class.ng-hide')
    get getNgHide() {
        return this.isActiveHideUp && (!this.isOpen() || !this._initialized);
    }

    @HostBinding('class.ng-hide-animate')
    get getNgHideAnimate() {
        return !this.zippyAnimationOff && this._initialized;
    }

    /** Stream of content animation done events. */
    zippyAnimationDone = new Subject<AnimationEvent>();

    private _initialized = false;

    override ngOnInit(): void {
        // We need a Subject with distinctUntilChanged, because the `done` event
        // fires twice on some browsers. See https://github.com/angular/angular/issues/24084
        this.zippyAnimationDone
            .pipe(
                distinctUntilChanged((x, y) => {
                    return x.fromState === y.fromState && x.toState === y.toState;
                })
            )
            .subscribe(event => {
                if (event.toState === 'expanded') {
                    this.afterExpand.emit();

                    if (this.zippyScrollIntoView) {
                        this.scrollElementIntoView();
                    }
                } else if (event.toState === 'collapsed') {
                    this.afterCollapse.emit();
                }
            });

        super.ngOnInit();
    }

    override ngOnDestroy(): void {
        this.zippyAnimationDone.complete();

        super.ngOnDestroy();
    }

    protected override setVisibility(to: boolean): void {
        if (this._zippyOff) {
            to = true;
        }

        super.setVisibility(to);

        this.playerState = to || this.zippyAnimationOff ? 'expanded' : 'collapsed';

        this.timeoutService.setTimeout(() => {
            this._initialized = true;
        });

        this.detectChanges();
    }

    /** Scrolls element into view. */
    protected scrollElementIntoView(): void {
        const minVal = this.zippyScrollIntoView?.offsetY || 300;
        const rect = this.ngEl.nativeElement.getBoundingClientRect();
        if (rect.top < minVal || rect.top + 200 > window.innerHeight) {
            const options = {
                duration: this.zippyScrollIntoView?.duration || 1,
                delay: this.zippyScrollIntoView?.delay || 0,
                scrollTo: {
                    y: this.ngEl?.nativeElement,
                    offsetY: minVal
                },
                ease: 'power2'
            };

            gsap.to(window, options);
        }
    }
}
